import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '@/lib/api';
import { Package, Search, CheckCircle2, Circle, Clock, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/track')({
  component: TrackPage,
});

function TrackPage() {
  const search: any = Route.useSearch();
  const initialCode = (search.code || '').trim();
  
  // Use a single query as the source of truth
  const { 
    data, 
    isLoading, 
    error: queryError,
    isRefetching 
  } = useQuery({
    queryKey: ['public-order', initialCode],
    queryFn: async () => {
      if (!initialCode) return null;
      const res = await fetch(`${API_URL}/public/orders/${encodeURIComponent(initialCode)}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Order not found');
      return json.data;
    },
    enabled: !!initialCode,
    retry: false,
    staleTime: 30000, // 30 seconds fresh
  });

  const error = queryError ? (queryError as Error).message : null;
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const input = form.elements.namedItem('trackingCode') as HTMLInputElement;
    const newCode = input.value.trim();
    
    if (newCode) {
      // Update URL search params to trigger useQuery via initialCode
      window.history.replaceState(null, '', `/track?code=${encodeURIComponent(newCode)}`);
      // Force reload if needed, though react-router might handle it.
      // Better: use navigate from router if available, but for now we rely on queryKey sync.
      window.location.reload(); 
    }
  };

  const statusColors: Record<string, string> = {
    DRAFT: "bg-muted text-muted-foreground",
    PROCESSING: "bg-blue-100 text-blue-700 border-blue-200",
    READY: "bg-amber-100 text-amber-700 border-amber-200",
    COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-12 px-4 shadow-inner">
      <div className="max-w-md w-full mx-auto space-y-6">
        
        {/* Header / Brand */}
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center relative">
              <Package className="h-8 w-8 text-primary relative z-10" />
              {isLoading && (
                <div className="absolute inset-0 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
              )}
            </div>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Track Your Order</h1>
          <p className="text-muted-foreground text-sm">
            Enter your invoice number below.
          </p>
        </div>

        {/* Search Box */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input 
            name="trackingCode"
            autoFocus
            defaultValue={initialCode}
            className="h-12 bg-white shadow-sm"
            placeholder="e.g. INV-2026-001" 
          />
          <Button type="submit" disabled={isLoading} className="h-12 w-12 shrink-0 p-0 shadow-sm">
            {isLoading || isRefetching ? <RotateCcw className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          </Button>
        </form>

        {error && (
          <div className="p-4 bg-destructive/10 text-destructive text-sm font-medium rounded-lg text-center animate-in fade-in slide-in-from-top-1">
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Status Summary */}
            <Card className="border-t-4 border-t-primary shadow-md overflow-hidden relative">
              {/* Glassmorphic Pulse Background for active status */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl motion-safe:animate-pulse" />
              
              <CardHeader className="bg-muted/30 pb-4 border-b relative z-10">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      Order {data.invoiceNo}
                    </CardTitle>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {data.customerName || "Walk-in Customer"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {data.tenantName}
                    </p>
                  </div>
                  <Badge variant="outline" className={`capitalize shadow-sm ${statusColors[data.status] || ''}`}>
                    {data.status.toLowerCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-6 px-6 relative z-10">
                
                {/* Visual Timeline */}
                <div className="relative pl-6 border-l-2 border-primary/20 space-y-8 pb-4">
                  {/* Order Placed */}
                  <div className="relative">
                    <div className="absolute -left-[35px] bg-white p-1 rounded-full border-2 border-primary mr-4 text-primary">
                       <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none">Order Placed</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(data.date).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Processing / Steps */}
                  <div className="relative">
                    <div className="absolute -left-[35px] bg-white p-1 rounded-full border-2 border-primary mr-4 text-primary">
                       {data.status === 'DRAFT' ? <Circle className="h-4 w-4" /> : <RotateCcw className={`h-4 w-4 ${data.status === 'PROCESSING' ? 'animate-spin' : ''}`} />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none">Processing & Washing</p>
                      
                      {/* Sub-steps */}
                      {data.steps && data.steps.length > 0 && (
                        <div className="mt-3 space-y-3 bg-muted/40 p-3 rounded-lg text-xs border border-white backdrop-blur-sm">
                          {data.steps.map((step: any, i: number) => (
                            <div key={i} className="flex justify-between items-center group">
                              <span className="font-medium capitalize flex items-center gap-1.5 text-foreground/80">
                                {step.completedAt ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Clock className="h-3 w-3 text-muted-foreground" />} 
                                {step.step}
                              </span>
                              <span className="text-muted-foreground">
                                {step.completedAt ? `Finished ${new Date(step.completedAt).toLocaleTimeString([], {timeStyle: 'short'})}` : "Pending"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Ready */}
                  <div className="relative">
                    <div className="absolute -left-[35px] bg-white p-1 rounded-full border-2 border-primary mr-4 text-primary">
                       {['READY', 'COMPLETED', 'DELIVERED'].includes(data.status) ? <CheckCircle2 className="h-4 w-4 shadow-[0_0_10px_rgba(var(--primary),0.2)]" /> : <Circle className="h-4 w-4 text-muted-foreground/30 border-none" />}
                    </div>
                    <div>
                      <p className={`text-sm font-medium leading-none ${!['READY', 'COMPLETED', 'DELIVERED'].includes(data.status) && "text-muted-foreground"}`}>
                        Ready for Pickup
                      </p>
                      {['READY', 'COMPLETED', 'DELIVERED'].includes(data.status) && (
                         <div className="mt-1 flex items-center gap-2">
                           <p className="text-xs text-emerald-600 font-bold motion-safe:animate-pulse">Your laundry is ready!</p>
                           <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 motion-safe:animate-ping" />
                         </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Delivered */}
                  <div className="relative">
                    <div className="absolute -left-[35px] bg-white p-1 rounded-full border-2 border-primary mr-4 text-primary">
                       {['COMPLETED', 'DELIVERED'].includes(data.status) ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground/30 border-none" />}
                    </div>
                    <div>
                      <p className={`text-sm font-medium leading-none ${!['COMPLETED', 'DELIVERED'].includes(data.status) && "text-muted-foreground"}`}>
                        Order Finished
                      </p>
                    </div>
                  </div>

                </div>

              </CardContent>
            </Card>
            
            <div className="text-center text-xs text-muted-foreground pt-4 pb-12">
              Payment Status: <Badge variant="secondary" className="font-bold ml-1 bg-white border border-slate-200 text-slate-700">{data.paymentStatus}</Badge>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
